import { useAtomValue } from 'jotai';
import Sidebar from './Sidebar';
import { MobileSidebar } from './Sidebar';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authAtom } from '../../../store/auth';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Header } from './Header';
import { classNames } from '../../../utils';
import NotificationsBridge from '../../Notifications/NotificationsBridge';
import { rememberPostLoginPath } from '../../../utils/postLoginRedirect';
import { useMyAccess } from '../../../Hooks/usePermissions';
import { canOpenPath, firstAllowedPath, pageKeyForPath } from '../../../utils/pageAccess';
import NoAccess from '../../NoAccess';
import PageLoader from '../../PageLoader';

const Main = ({ children }) => {
  const auth = useAtomValue(authAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const arrivedSignedOut = useRef(!auth?.access_token);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { access, isLoading: accessLoading } = useMyAccess();
  const allowed = canOpenPath(access, location.pathname);
  const homePath = firstAllowedPath(access);
  const backorderLink =
    location.pathname === '/sourcing/requests' &&
    new URLSearchParams(location.search).get('source') === 'backorder';

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (!auth?.access_token) {
      if (arrivedSignedOut.current) {
        rememberPostLoginPath(`${location.pathname}${location.search}`);
      }
      navigate('/login');
    }
  }, [auth, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (backorderLink) navigate('/sourcing/backorders', { replace: true });
  }, [backorderLink, navigate]);

  useEffect(() => {
    if (access && location.pathname === '/' && !allowed && homePath !== '/') {
      navigate(homePath, { replace: true });
    }
  }, [access, allowed, homePath, location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {auth?.access_token && <NotificationsBridge />}
      <MobileSidebar />
      <Sidebar
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />
      <Header
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      {/* Main Content */}
      <div
        className={classNames(
          /* overflow-x-auto, not overflow-hidden: a table wider than a
             phone used to be cut off at the edge with no way to reach the
             rest of it. Now it scrolls. */
          'px-4 py-[20px] min-h-screen transition-all duration-300 pt-24 overflow-x-auto',
          isCollapsed ? 'lg:ml-[6.4375rem]' : 'lg:ml-[260px]'
        )}
      >
        {backorderLink || (accessLoading && pageKeyForPath(location.pathname)) ? (
          /* The same loader every other screen shows, and it says what it is
             waiting for. A bare spinner in the middle of an empty page is
             what makes a slow check look like a broken one. */
          <PageLoader label="Checking your access…" />
        ) : allowed ? (
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        ) : (
          <NoAccess homePath={homePath} />
        )}
      </div>
    </div>
  );
};

export default Main;
